import { flow, pipe } from 'fp-ts/function';
import { isEmpty } from 'lodash';
import * as Opt from 'fp-ts/Option';
import { match } from 'ts-pattern';
import {
  FroInsightsData,
  MessageMediumMetrics,
  MessageMediumTab,
  MessageTypeMetrics,
  MessageTypeRowData,
} from 'modules/intelligentInsights/frequencyOptimization/types';
import {
  BREAKDOWN_AUDIENCE_ENGAGEMENT_METRICS,
  BREAKDOWN_AUDIENCE_MESSAGE_TYPE_CAMPAIGN_DEFAULT_METRIC_TEXT,
} from 'modules/intelligentInsights/constants';
import { getCompactNumberFormat } from 'utils/l10n/locale';
import { localizePercent } from 'utils/l10n';
import { DEFAULT_LOCALE } from 'utils/l10n/constants';
import { MessageMedium } from 'constants/messageMediums';
import { formatAvgSendsDiff, truncateNumber } from 'modules/intelligentInsights/domain.utils';
import { doesExist, Nullable } from 'utils/tsHelpers';
import {
  ConfigByMedium,
  FrequencyManagementMessageType,
} from 'modules/frequencyManagement/lib/types';
import { MediumConfig } from 'modules/frequencyManagement/types';

export type WeeklySends = {
  weeklyHoldoutSendsPerUser: number;
  weeklyOptimizedSendsPerUser: number;
};

export type AudienceSizeData = {
  optimizedUsers: string;
  percentTotal: string;
};

export const formatEngagementLift = (metricValue: number) => localizePercent(metricValue);

export const getByMediumBreakdown = (coreData: FroInsightsData, messageMedium: MessageMediumTab) =>
  Opt.fromNullable(
    coreData?.messageMediumData.find((item) => item.messageMedium === messageMedium),
  );

export const getByMessageTypesBreakdown = (
  coreData: FroInsightsData,
  messageMedium: MessageMediumTab,
) =>
  pipe(
    Opt.fromNullable(
      coreData?.messageTypeData.filter((item) => item.messageMedium === messageMedium),
    ),
    Opt.chain((maybeMessageTypes: Array<MessageTypeMetrics>) => {
      return isEmpty(maybeMessageTypes) ? Opt.none : Opt.some(maybeMessageTypes);
    }),
  );

export const getFrMSettingsByMedium = (
  frmSettings: ConfigByMedium,
  messageMedium: MessageMediumTab,
) => Opt.fromNullable(frmSettings[messageMedium]);

export const getWeeklySends = (messageMediumBreakdown: Opt.Option<MessageMediumMetrics>) =>
  Opt.map<MessageMediumMetrics, WeeklySends>((mediumMetrics) => {
    return {
      weeklyHoldoutSendsPerUser: mediumMetrics.weeklyHoldoutSendsPerUser,
      weeklyOptimizedSendsPerUser: mediumMetrics.weeklyOptimizedSendsPerUser,
    };
  })(messageMediumBreakdown);

export const getWeeklySendsByMedium = flow(
  getByMediumBreakdown,
  getWeeklySends,
  Opt.getOrElse(() => ({ weeklyHoldoutSendsPerUser: 0, weeklyOptimizedSendsPerUser: 0 })),
);

export const getAvgWeeklySendsByMedium = flow(
  getWeeklySendsByMedium,
  ({ weeklyHoldoutSendsPerUser, weeklyOptimizedSendsPerUser }) =>
    formatAvgSendsDiff(weeklyHoldoutSendsPerUser, weeklyOptimizedSendsPerUser),
);

export const defaultMessageTypesRowData = {
  messageTypeId: 0,
  holdoutSends: 0,
  optimizedSends: 0,
  holdoutOpens: null,
  optimizedOpens: null,
  holdoutClicks: null,
  optimizedClicks: null,
  holdoutUnsubscribes: 0,
  optimizedUnsubscribes: 0,
  holdoutSendSkips: 0,
  optimizedSendSkips: 0,
  holdoutOpenRate: null,
  optimizedOpenRate: null,
  holdoutClickRate: null,
  optimizedClickRate: null,
  holdoutUnsubscribeRate: 0,
  optimizedUnsubscribeRate: 0,
  // Need nulls for all displayed MT row data to indicate no value
  openRateLift: null,
  clickRateLift: null,
  unsubscribeRateLift: null as any,
  messagesSkipped: null as any,
  holdoutSendSkipRate: 0,
  optimizedSendSkipRate: 0,
  name: '',
  topCampaignIds: [],
  campaigns: [],
} as MessageTypeRowData;

export const defaultMessageTypeSetting = {
  id: 0,
  name: '',
} as FrequencyManagementMessageType;

export const createMessageTypesGetOrElse = (messageMedium: MessageMediumTab) =>
  Opt.getOrElse(() => [
    {
      ...defaultMessageTypesRowData,
      messageMedium,
    } as MessageTypeRowData,
  ]);

export const formatMessageTypeAvailableLift = (liftValue: Nullable<number>) =>
  doesExist(liftValue)
    ? localizePercent(liftValue as number)
    : BREAKDOWN_AUDIENCE_MESSAGE_TYPE_CAMPAIGN_DEFAULT_METRIC_TEXT;

export const truncateLiftForExport = (liftValue: Nullable<number>) =>
  doesExist(liftValue) ? truncateNumber(liftValue as number, 4) : '';

type CombinedSourceData = {
  frmMediumSettings: MediumConfig;
  messageTypes: MessageTypeMetrics[];
};
export const getMessageTypesRowData = ({
  frmMediumSettings,
  messageTypes,
}: CombinedSourceData): Array<MessageTypeRowData> => {
  return messageTypes.map((messageType) => {
    const messageTypeConfig = frmMediumSettings.messageTypes.find(
      (config) => config.id === messageType.messageTypeId,
    );
    return {
      ...messageType,
      messagesSkipped: messageType.optimizedSendSkipRate,
      name: messageTypeConfig?.name || '',
      campaigns: [],
    };
  });
};

const combineMessageTypesData =
  (frmMediumSettings: Opt.Option<MediumConfig>) =>
  (messageTypes: Opt.Option<MessageTypeMetrics[]>): Opt.Option<CombinedSourceData> =>
    pipe(
      /* 
                Begin with FrM settings, and provide a default value if none are loaded
                because we still want to display FrO data
              */
      frmMediumSettings,
      Opt.alt(() =>
        Opt.some({
          messageTypes: [defaultMessageTypeSetting],
          isConfiguredForSending: false,
          settings: {} as any,
        }),
      ),
      Opt.chain((frm) =>
        pipe(
          messageTypes,
          Opt.map((mt) => ({
            messageTypes: mt,
            frmMediumSettings: frm,
          })),
        ),
      ),
    );

export const getMessageTypesByMedium = (
  coreData: FroInsightsData,
  frmSettings: ConfigByMedium,
  messageMedium: MessageMediumTab,
) => {
  const combinedData = pipe(
    getByMessageTypesBreakdown(coreData, messageMedium),
    combineMessageTypesData(getFrMSettingsByMedium(frmSettings, messageMedium)),
  );
  const messageTypeRows = Opt.map(getMessageTypesRowData)(combinedData);
  return createMessageTypesGetOrElse(messageMedium)(messageTypeRows);
};

export const engagementMetricsByMedium: Record<
  MessageMediumTab,
  { metricKeys: Array<keyof MessageMediumMetrics>; metricDisplayNames: Array<string> }
> = {
  [MessageMedium.Email]: {
    metricKeys: ['openRateLift', 'clickRateLift', 'unsubscribeRateLift'],
    metricDisplayNames: [
      BREAKDOWN_AUDIENCE_ENGAGEMENT_METRICS.OPEN_RATE,
      BREAKDOWN_AUDIENCE_ENGAGEMENT_METRICS.CLICK_RATE,
      BREAKDOWN_AUDIENCE_ENGAGEMENT_METRICS.UNSUBSCRIBE_RATE,
    ],
  },
  [MessageMedium.Push]: {
    metricKeys: ['openRateLift', 'unsubscribeRateLift'],
    metricDisplayNames: [
      BREAKDOWN_AUDIENCE_ENGAGEMENT_METRICS.OPEN_RATE,
      BREAKDOWN_AUDIENCE_ENGAGEMENT_METRICS.UNSUBSCRIBE_RATE,
    ],
  },
  [MessageMedium.SMS]: {
    metricKeys: ['clickRateLift', 'unsubscribeRateLift'],
    metricDisplayNames: [
      BREAKDOWN_AUDIENCE_ENGAGEMENT_METRICS.CLICK_RATE,
      BREAKDOWN_AUDIENCE_ENGAGEMENT_METRICS.UNSUBSCRIBE_RATE,
    ],
  },
};
type BreakdownAudienceEngagementConfigPair = [Array<string>, (keyof MessageMediumMetrics)[]];
export const getBreakdownAudienceEngagementConfig = (
  messageMedium: MessageMediumTab,
): [Array<string>, Array<keyof MessageMediumMetrics>] => {
  const createAudienceEngagementConfigPair = (messageMedium: MessageMediumTab) => {
    const { metricKeys, metricDisplayNames } = engagementMetricsByMedium[messageMedium];
    return [metricDisplayNames, metricKeys] as BreakdownAudienceEngagementConfigPair;
  };

  return match(messageMedium)
    .with(MessageMedium.Email, () => createAudienceEngagementConfigPair(MessageMedium.Email))
    .with(MessageMedium.Push, () => createAudienceEngagementConfigPair(MessageMedium.Push))
    .with(MessageMedium.SMS, () => createAudienceEngagementConfigPair(MessageMedium.SMS))
    .otherwise(() => createAudienceEngagementConfigPair(MessageMedium.Email));
};

export const calculateEngagementLift =
  (messageMedium: MessageMediumTab) => (engagementMetrics: Opt.Option<MessageMediumMetrics>) => {
    return Opt.map((engagementMetrics: MessageMediumMetrics) => {
      const [, metricKeys] = getBreakdownAudienceEngagementConfig(messageMedium);
      return metricKeys.reduce(
        (acc, metricKey) => {
          const metricValue = engagementMetrics[metricKey];
          return {
            ...acc,
            [metricKey]: metricValue,
          };
        },
        {} as Record<keyof MessageMediumMetrics, number>,
      );
    })(engagementMetrics);
  };

export const getDefaultEnagementMetricsLift = (messageMedium: MessageMediumTab) => {
  return Opt.getOrElse(() => {
    const [, metricKeys] = getBreakdownAudienceEngagementConfig(messageMedium);
    return metricKeys.reduce(
      (acc, metricKey) => {
        return {
          ...acc,
          [metricKey]: 0,
        };
      },
      {} as Record<keyof MessageMediumMetrics, number>,
    );
  });
};

export const getEngagementLiftByMedium = (
  coreData: FroInsightsData,
  messageMedium: MessageMediumTab,
) =>
  pipe(
    getByMediumBreakdown(coreData, messageMedium),
    calculateEngagementLift(messageMedium),
    getDefaultEnagementMetricsLift(messageMedium),
  );

export const calculateOptimizedAudienceSize = (
  messageMediumBreakdown: Opt.Option<MessageMediumMetrics>,
) =>
  Opt.map<MessageMediumMetrics, AudienceSizeData>(({ optimizedUsers, holdoutUsers }) => {
    const rawTotal = optimizedUsers / (optimizedUsers + holdoutUsers);
    return {
      optimizedUsers: getCompactNumberFormat(DEFAULT_LOCALE, { maximumFractionDigits: 1 }).format(
        optimizedUsers,
      ),
      percentTotal: localizePercent(Number.isNaN(rawTotal) ? 0 : rawTotal),
    };
  })(messageMediumBreakdown);

export const getOptimizedAudienceSize = flow(
  getByMediumBreakdown,
  calculateOptimizedAudienceSize,
  Opt.getOrElse(() => ({ optimizedUsers: '0', percentTotal: '0%' })),
);

export const getFrMSettingsByMediumIsConfigured = Opt.map(
  (mediumConfig: MediumConfig) => mediumConfig.isConfiguredForSending,
);

export const getMediumSettingsIsConfigured = flow(
  getFrMSettingsByMedium,
  getFrMSettingsByMediumIsConfigured,
  Opt.getOrElse(() => false),
);

export const getMediumHasOptimizeData = (
  coreData: FroInsightsData,
  messageMedium: MessageMediumTab,
) => {
  return (
    Opt.isSome(getByMediumBreakdown(coreData, messageMedium)) ||
    Opt.isSome(getByMessageTypesBreakdown(coreData, messageMedium))
  );
};

export const getMessageTypesForExport = (messageTypeRows: MessageTypeRowData[]) =>
  messageTypeRows.map((messageType) => {
    return {
      name: messageType.name || '',
      openRateLift: truncateLiftForExport(messageType.openRateLift),
      clickRateLift: truncateLiftForExport(messageType.clickRateLift),
      unsubscribeRateLift: truncateLiftForExport(messageType.unsubscribeRateLift),
      topCampaigns: (messageType.topCampaignIds || []).join(', '),
    };
  });
